<template>
  <div class="admin-container">
    <AdminSidebar :isOpen="isSidebarOpen" :userData="currentUser" />

    <!-- Main Content -->
    <div class="main-content" :class="{ 'content-shifted': isSidebarOpen }">
      <nav class="admin-nav">
        <button class="menu-btn" @click="toggleSidebar">
          <i class="fas fa-bars"></i>
        </button>
      </nav>

      <div class="admin-content">
        <div class="discount-codes-container">
          <div class="header">
            <h1>Quản lý mã giảm giá</h1>
            <button class="add-btn" @click="openAddModal">
              <i class="fas fa-plus"></i> Thêm mã giảm giá
            </button>
          </div>

          <div class="filters">
            <div class="search-box">
              <i class="fas fa-search"></i>
              <input
                type="text"
                v-model="searchQuery"
                placeholder="Tìm kiếm theo mã giảm giá..."
                @input="handleSearch"
              />
            </div>
            <div class="filter-toggle">
              <button
                :class="['filter-btn', { active: showActive }]"
                @click="toggleActiveFilter(true)"
              >
                <i class="fas fa-check-circle"></i> Đang hoạt động
              </button>
              <button
                :class="['filter-btn', { active: !showActive }]"
                @click="toggleActiveFilter(false)"
              >
                <i class="fas fa-times-circle"></i> Đã vô hiệu hóa
              </button>
            </div>
          </div>

          <!-- Table Container -->
          <div class="table-container">
            <!-- Loading State -->
            <div v-if="isLoading" class="table-loading">
              <div class="table-spinner"></div>
              <p>Đang tải dữ liệu...</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="filteredDiscountCodes.length === 0" class="table-empty">
              <i class="fas fa-ticket-alt table-empty-icon"></i>
              <h3 class="table-empty-title">Không có mã giảm giá nào</h3>
              <p class="table-empty-text">{{ showActive ? 'Chưa có mã giảm giá nào đang hoạt động' : 'Không có mã giảm giá nào đã vô hiệu hóa' }}</p>
              <button class="table-empty-action" @click="openAddModal">
                <i class="fas fa-plus"></i> Thêm mã giảm giá
              </button>
            </div>

            <!-- Table Content -->
            <div v-else class="table-responsive has-mobile-cards">
              <!-- Desktop Table -->
              <table class="admin-table">
                <thead>
                  <tr>
                    <th>Mã giảm giá</th>
                    <th>Loại</th>
                    <th class="text-right">Giá trị</th>
                    <th class="text-right">Đơn tối thiểu</th>
                    <th>Thời gian hiệu lực</th>
                    <th class="text-center">Giới hạn</th>
                    <th class="text-center">Đã sử dụng</th>
                    <th>Trạng thái</th>
                    <th class="col-actions">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="discount in paginatedDiscountCodes"
                    :key="discount.discount_id"
                    :class="{ 'internal-row': discount.is_internal }"
                  >
                    <td class="code-cell">
                      <div class="code-wrapper">
                        <span class="discount-code">{{ discount.code }}</span>
                        <span v-if="discount.is_internal" class="internal-badge" title="Mã nội bộ - Tự động xác nhận vé">
                          🔐 INTERNAL
                        </span>
                      </div>
                      <small v-if="discount.description" class="description-text">
                        {{ discount.description }}
                      </small>
                    </td>
                    <td class="nowrap">{{ getDiscountTypeName(discount.discount_type) }}</td>
                    <td class="text-right nowrap">
                      <span class="price-value">
                        {{
                          discount.discount_type === 'percentage'
                            ? `${discount.discount_value}%`
                            : formatCurrency(discount.discount_value)
                        }}
                      </span>
                      <small
                        v-if="discount.discount_type === 'percentage' && discount.max_discount"
                        class="max-discount"
                      >
                        (tối đa {{ formatCurrency(discount.max_discount) }})
                      </small>
                    </td>
                    <td class="text-right nowrap">{{ formatCurrency(discount.min_order_value) }}</td>
                    <td>
                      <div class="date-range">
                        <span>{{ formatDate(discount.valid_from) }}</span>
                        <i class="fas fa-arrow-right"></i>
                        <span>{{
                          discount.valid_until
                            ? formatDate(discount.valid_until)
                            : 'Không giới hạn'
                        }}</span>
                      </div>
                      <div
                        class="expiry-status"
                        :class="getExpiryStatusClass(discount)"
                      >
                        {{ getExpiryStatus(discount) }}
                      </div>
                    </td>
                    <td class="text-center">
                      {{
                        discount.usage_limit > 0
                          ? discount.usage_limit
                          : 'Không giới hạn'
                      }}
                    </td>
                    <td class="text-center">
                      <span :class="{ 'usage-warning': isNearLimit(discount) }">
                        {{ discount.usage_count }}
                        <span v-if="discount.usage_limit > 0">/ {{ discount.usage_limit }}</span>
                      </span>
                    </td>
                    <td>
                      <span :class="['status-badge', discount.is_active ? 'active' : 'inactive']">
                        {{ discount.is_active ? 'Hoạt động' : 'Vô hiệu' }}
                      </span>
                    </td>
                    <td class="col-actions">
                      <div class="action-buttons">
                        <button
                          class="action-btn edit-btn"
                          @click="openEditModal(discount)"
                          title="Chỉnh sửa"
                          aria-label="Chỉnh sửa mã giảm giá"
                        >
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          class="action-btn"
                          :class="discount.is_active ? 'delete-btn' : 'view-btn'"
                          @click="toggleDiscountStatus(discount)"
                          :title="discount.is_active ? 'Vô hiệu hóa' : 'Kích hoạt'"
                          :aria-label="discount.is_active ? 'Vô hiệu hóa mã giảm giá' : 'Kích hoạt mã giảm giá'"
                        >
                          <i :class="discount.is_active ? 'fas fa-ban' : 'fas fa-check'"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Mobile Card View -->
              <div class="mobile-card-list">
                <div 
                  v-for="discount in paginatedDiscountCodes" 
                  :key="'card-' + discount.discount_id" 
                  class="mobile-card"
                  :class="{ 'internal-card': discount.is_internal }"
                >
                  <div class="mobile-card-header">
                    <div class="mobile-card-title">
                      <span class="discount-code">{{ discount.code }}</span>
                      <span v-if="discount.is_internal" class="internal-badge">🔐</span>
                    </div>
                    <span :class="['status-badge', discount.is_active ? 'active' : 'inactive']">
                      {{ discount.is_active ? 'Hoạt động' : 'Vô hiệu' }}
                    </span>
                  </div>
                  <div class="mobile-card-body">
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Giảm giá:</span>
                      <span class="mobile-card-value price-value">
                        {{
                          discount.discount_type === 'percentage'
                            ? `${discount.discount_value}%`
                            : formatCurrency(discount.discount_value)
                        }}
                        <small v-if="discount.discount_type === 'percentage' && discount.max_discount">
                          (max {{ formatCurrency(discount.max_discount) }})
                        </small>
                      </span>
                    </div>
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Đơn tối thiểu:</span>
                      <span class="mobile-card-value">{{ formatCurrency(discount.min_order_value) }}</span>
                    </div>
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Sử dụng:</span>
                      <span class="mobile-card-value" :class="{ 'usage-warning': isNearLimit(discount) }">
                        {{ discount.usage_count }}
                        <span v-if="discount.usage_limit > 0">/ {{ discount.usage_limit }}</span>
                        <span v-else> (không giới hạn)</span>
                      </span>
                    </div>
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Hiệu lực:</span>
                      <span class="mobile-card-value">
                        <span :class="['expiry-status', getExpiryStatusClass(discount)]">
                          {{ getExpiryStatus(discount) }}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div class="mobile-card-actions">
                    <button class="action-btn edit-btn with-label" @click="openEditModal(discount)">
                      <i class="fas fa-edit"></i> Sửa
                    </button>
                    <button 
                      class="action-btn with-label"
                      :class="discount.is_active ? 'delete-btn' : 'view-btn'"
                      @click="toggleDiscountStatus(discount)"
                    >
                      <i :class="discount.is_active ? 'fas fa-ban' : 'fas fa-check'"></i>
                      {{ discount.is_active ? 'Vô hiệu' : 'Kích hoạt' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination-container" v-if="!isLoading && filteredDiscountCodes.length > 0">
            <div class="pagination-info">
              Hiển thị {{ paginatedDiscountCodes.length }} / {{ filteredDiscountCodes.length }} mã giảm giá
            </div>
            <div class="pagination-controls">
              <button
                class="pagination-btn"
                @click="changePage(currentPage - 1)"
                :disabled="currentPage === 1"
              >
                <i class="fas fa-chevron-left"></i> Trước
              </button>

              <span class="page-info">
                Trang {{ currentPage }} / {{ totalPages || 1 }}
              </span>

              <button
                class="pagination-btn"
                @click="changePage(currentPage + 1)"
                :disabled="currentPage >= totalPages"
              >
                Sau <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Đặt modal ở đây, ngoài các container lồng nhau -->
    <div class="modal" v-if="showModal" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2><i :class="isEditing ? 'fas fa-edit' : 'fas fa-plus-circle'"></i> {{ isEditing ? 'Chỉnh sửa' : 'Thêm' }} mã giảm giá</h2>
          <button class="close-btn" @click="closeModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveDiscountCode">
            <!-- Form row 1: Mã giảm giá và Loại giảm giá -->
            <div class="form-row">
              <div class="form-group">
                <label for="code">Mã giảm giá<span class="required">*</span></label>
                <div class="input-with-icon">
                  <i class="fas fa-tag input-icon"></i>
                  <input
                    type="text"
                    id="code"
                    v-model="discountForm.code"
                    required
                    :disabled="isEditing"
                    class="uppercase"
                    placeholder="VD: SUMMER2023"
                  />
                </div>
                <small v-if="isEditing" class="helper-text">Không thể thay đổi mã sau khi đã tạo</small>
              </div>
              <div class="form-group">
                <label for="discount_type">Loại giảm giá<span class="required">*</span></label>
                <div class="input-with-icon">
                  <i class="fas fa-percentage input-icon"></i>
                  <select id="discount_type" v-model="discountForm.discount_type" required>
                    <option value="percentage">Phần trăm (%)</option>
                    <option value="fixed">Số tiền cố định (VNĐ)</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Form row 2: Giá trị giảm giá và Giảm giá tối đa -->
            <div class="form-row">
              <div class="form-group">
                <label for="discount_value">
                  {{ discountForm.discount_type === 'percentage' ? 'Phần trăm giảm giá' : 'Số tiền giảm giá' }}
                  <span class="required">*</span>
                </label>
                <div class="input-with-suffix">
                  <input
                    type="number"
                    id="discount_value"
                    v-model.number="discountForm.discount_value"
                    required
                    min="0"
                    :max="discountForm.discount_type === 'percentage' ? 100 : null"
                    step="any"
                  />
                  <span class="suffix">{{ discountForm.discount_type === 'percentage' ? '%' : 'VNĐ' }}</span>
                </div>
              </div>
              <div class="form-group" v-if="discountForm.discount_type === 'percentage'">
                <label for="max_discount">Giảm tối đa</label>
                <div class="input-with-suffix">
                  <i class="fas fa-money-bill-wave input-icon"></i>
                  <input
                    type="number"
                    id="max_discount"
                    v-model.number="discountForm.max_discount"
                    min="0"
                    step="1000"
                    placeholder="Không giới hạn"
                  />
                  <span class="suffix">VNĐ</span>
                </div>
              </div>
            </div>

            <!-- Form row 3: Giá trị đơn hàng tối thiểu và Giới hạn sử dụng -->
            <div class="form-row">
              <div class="form-group">
                <label for="min_order_value">Giá trị đơn hàng tối thiểu</label>
                <div class="input-with-suffix">
                  <i class="fas fa-shopping-cart input-icon"></i>
                  <input
                    type="number"
                    id="min_order_value"
                    v-model.number="discountForm.min_order_value"
                    min="0"
                    step="1000"
                  />
                  <span class="suffix">VNĐ</span>
                </div>
              </div>
              <div class="form-group">
                <label for="usage_limit">Giới hạn sử dụng</label>
                <div class="input-with-icon">
                  <i class="fas fa-user-check input-icon"></i>
                  <input
                    type="number"
                    id="usage_limit"
                    v-model.number="discountForm.usage_limit"
                    min="0"
                    step="1"
                    placeholder="0 = Không giới hạn"
                  />
                </div>
                <small class="helper-text">Để 0 = không giới hạn số lần sử dụng</small>
              </div>
            </div>

            <!-- Form row 4: Thời gian hiệu lực -->
            <div class="form-row">
              <div class="form-group">
                <label for="valid_from">Ngày bắt đầu<span class="required">*</span></label>
                <div class="input-with-icon">
                  <i class="far fa-calendar-alt input-icon"></i>
                  <input
                    type="datetime-local"
                    id="valid_from"
                    v-model="discountForm.valid_from"
                    required
                  />
                </div>
              </div>
              <div class="form-group">
                <label for="valid_until">Ngày kết thúc</label>
                <div class="input-with-icon">
                  <i class="far fa-calendar-check input-icon"></i>
                  <input
                    type="datetime-local"
                    id="valid_until"
                    v-model="discountForm.valid_until"
                    :min="discountForm.valid_from"
                  />
                </div>
                <small class="helper-text">Để trống = không giới hạn thời gian</small>
              </div>
            </div>

            <!-- 🔐 Mã nội bộ (Internal Code) -->
            <div class="form-group toggle-container internal-toggle">
              <div class="toggle-switch">
                <input type="checkbox" id="is_internal" v-model="discountForm.is_internal" />
                <label for="is_internal"></label>
              </div>
              <div class="toggle-label">
                <span>🔐 Mã nội bộ (Internal)</span>
                <small v-if="discountForm.is_internal" class="internal-note">
                  ⚠️ Mã nội bộ sẽ tự động xác nhận vé (không cần thanh toán). 
                  Phải giảm giá 100% để kích hoạt tính năng này.
                </small>
                <small v-else>
                  Bật để đánh dấu đây là mã giảm giá nội bộ cho nhân viên/khách mời
                </small>
              </div>
            </div>

            <!-- Mô tả mã giảm giá -->
            <div class="form-group">
              <label for="description">Mô tả mã giảm giá</label>
              <div class="input-with-icon">
                <i class="fas fa-info-circle input-icon"></i>
                <input
                  type="text"
                  id="description"
                  v-model="discountForm.description"
                  placeholder="VD: Mã nội bộ cho nhân viên công ty"
                  maxlength="255"
                />
              </div>
              <small class="helper-text">Mô tả ngắn gọn về mục đích sử dụng mã này</small>
            </div>

            <!-- Kích hoạt mã giảm giá -->
            <div class="form-group toggle-container">
              <div class="toggle-switch">
                <input type="checkbox" id="is_active" v-model="discountForm.is_active" />
                <label for="is_active"></label>
              </div>
              <div class="toggle-label">
                <span>Kích hoạt mã giảm giá</span>
                <small>{{ discountForm.is_active ? 'Mã giảm giá đang hoạt động' : 'Mã giảm giá đang bị vô hiệu hóa' }}</small>
              </div>
            </div>

            <!-- Nút hành động -->
            <div class="form-actions">
              <button type="button" class="cancel-btn" @click="closeModal">
                <i class="fas fa-times"></i> Hủy
              </button>
              <button type="submit" class="save-btn" :disabled="isSaving">
                <i class="fas fa-spinner fa-spin" v-if="isSaving"></i>
                <i class="fas fa-save" v-else></i>
                {{ isSaving ? 'Đang lưu...' : 'Lưu mã giảm giá' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '@/plugins/axios'
import AdminSidebar from '../common/AdminSidebar.vue'
import { ToastService } from '@/services/ToastService'

const isProd = process.env.VUE_APP_IS_PROD === 'true'

const API_BASE_URL = isProd
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV

export default {
  name: 'AdminDiscountCodes',
  components: {
    AdminSidebar
  },
  data () {
    return {
      discountCodes: [],
      isLoading: false,
      isSaving: false,
      showModal: false,
      isEditing: false,
      searchQuery: '',
      showActive: true,
      isSidebarOpen: false,
      currentUser: {
        full_name: 'Admin User',
        role: 'Administrator'
      },
      discountForm: {
        code: '',
        discount_type: 'percentage',
        discount_value: 10,
        min_order_value: 0,
        max_discount: null,
        valid_from: this.getDefaultFromDate(),
        valid_until: null,
        usage_limit: 0,
        is_active: true,
        is_internal: false,  // 🔐 Field mới
        description: '',     // Field mới
        applicable_routes: null,
        applicable_ticket_types: null
      },
      originalDiscount: null,
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    userInitials () {
      return this.currentUser.full_name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },
    filteredDiscountCodes () {
      let filtered = this.discountCodes

      // Filter by active status
      filtered = filtered.filter(code => code.is_active === this.showActive)

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(code =>
          code.code.toLowerCase().includes(query)
        )
      }

      return filtered
    },
    totalPages() {
      return Math.ceil(this.filteredDiscountCodes.length / this.itemsPerPage)
    },
    paginatedDiscountCodes() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredDiscountCodes.slice(start, end)
    }
  },
  created() {
    this.fetchDiscountCodes()
    // this.getCurrentUser()
    const sidebarState = localStorage.getItem('sidebarOpen')
    if (sidebarState !== null) {
      this.isSidebarOpen = sidebarState === 'true'
    }
  },
  methods: {
    async fetchDiscountCodes () {
      try {
        this.isLoading = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/discount-codes/`,
          { headers }
        )

        this.discountCodes = response.data || []
      } catch (error) {
        console.error('Error fetching discount codes:', error)
        ToastService.error('Không thể tải danh sách mã giảm giá.')
      } finally {
        this.isLoading = false
      }
    },
    handleSearch () {
      // No need to do anything, as filteredDiscountCodes is reactive
    },
    toggleActiveFilter (showActive) {
      this.showActive = showActive
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen
      localStorage.setItem('sidebarOpen', this.isSidebarOpen)
    },
    // async getCurrentUser() {
    //   try {
    //     const token = localStorage.getItem('adminToken')
    //     if (!token) return
        
    //     const headers = { Authorization: `Bearer ${token}` }
    //     const response = await axiosInstance.get(
    //       `${API_BASE_URL}/admin/profile`,
    //       { headers }
    //     )
        
    //     this.currentUser = response.data
    //   } catch (error) {
    //     console.error('Error fetching admin profile:', error)
    //   }
    // },
    openAddModal () {
      this.isEditing = false
      this.discountForm = {
        code: '',
        discount_type: 'percentage',
        discount_value: 10,
        min_order_value: 0,
        max_discount: null,
        valid_from: this.getDefaultFromDate(),
        valid_until: null,
        usage_limit: 0,
        is_active: true,
        is_internal: false,  // 🔐 Field mới
        description: '',     // Field mới
        applicable_routes: null,
        applicable_ticket_types: null
      }
      this.showModal = true
    },
    openEditModal (discount) {
      this.isEditing = true
      this.originalDiscount = discount

      // Format dates for the datetime-local input
      const validFrom = discount.valid_from
        ? new Date(discount.valid_from).toISOString().slice(0, 16)
        : this.getDefaultFromDate()

      const validUntil = discount.valid_until
        ? new Date(discount.valid_until).toISOString().slice(0, 16)
        : null

      this.discountForm = {
        code: discount.code,
        discount_type: discount.discount_type,
        discount_value: discount.discount_value,
        min_order_value: discount.min_order_value,
        max_discount: discount.max_discount,
        valid_from: validFrom,
        valid_until: validUntil,
        usage_limit: discount.usage_limit,
        is_active: discount.is_active,
        is_internal: discount.is_internal || false,  // 🔐 Field mới
        description: discount.description || '',     // Field mới
        applicable_routes: discount.applicable_routes,
        applicable_ticket_types: discount.applicable_ticket_types
      }
      this.showModal = true
    },
    closeModal () {
      this.showModal = false
      this.originalDiscount = null
    },
    getDefaultFromDate () {
      const now = new Date()
      return now.toISOString().slice(0, 16)
    },
    formatDate (dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatCurrency (amount) {
      if (!amount && amount !== 0) return 'N/A'
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount)
    },
    getDiscountTypeName (type) {
      return type === 'percentage' ? 'Phần trăm' : 'Cố định'
    },
    getExpiryStatus (discount) {
      const now = new Date()
      const validFrom = new Date(discount.valid_from)

      if (validFrom > now) {
        return 'Sắp có hiệu lực'
      }

      if (!discount.valid_until) {
        return 'Không giới hạn'
      }

      const validUntil = new Date(discount.valid_until)
      if (validUntil < now) {
        return 'Đã hết hạn'
      }

      // Calculate days remaining
      const daysRemaining = Math.ceil(
        (validUntil - now) / (1000 * 60 * 60 * 24)
      )
      return `Còn ${daysRemaining} ngày`
    },
    getExpiryStatusClass (discount) {
      const now = new Date()
      const validFrom = new Date(discount.valid_from)

      if (validFrom > now) {
        return 'upcoming'
      }

      if (!discount.valid_until) {
        return 'unlimited'
      }

      const validUntil = new Date(discount.valid_until)
      if (validUntil < now) {
        return 'expired'
      }

      // Calculate days remaining
      const daysRemaining = Math.ceil(
        (validUntil - now) / (1000 * 60 * 60 * 24)
      )
      if (daysRemaining <= 3) {
        return 'expiring-soon'
      }

      return 'valid'
    },
    isNearLimit (discount) {
      if (!discount.usage_limit || discount.usage_limit === 0) return false

      // Near limit if used 80% or more
      return discount.usage_count / discount.usage_limit >= 0.8
    },
    async saveDiscountCode () {
      try {
        this.isSaving = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Convert form data to the format expected by the API
        const discountData = {
          ...this.discountForm,
          code: this.discountForm.code.toUpperCase(),
          // Convert date strings to ISO format
          valid_from: this.discountForm.valid_from
            ? new Date(this.discountForm.valid_from).toISOString()
            : null,
          valid_until: this.discountForm.valid_until
            ? new Date(this.discountForm.valid_until).toISOString()
            : null
        }

        if (this.isEditing) {
          // Update existing discount code
          await axiosInstance.put(
            `${API_BASE_URL}/admin/discount-codes/${this.originalDiscount.discount_id}`,
            discountData,
            { headers }
          )
          ToastService.success('Mã giảm giá đã được cập nhật thành công')
        } else {
          // Create new discount code
          await axiosInstance.post(
            `${API_BASE_URL}/admin/discount-codes/`,
            discountData,
            { headers }
          )
          ToastService.success('Mã giảm giá mới đã được tạo thành công')
        }

        // Reload discount codes and close modal
        await this.fetchDiscountCodes()
        this.closeModal()
      } catch (error) {
        console.error('Error saving discount code:', error)
        const errorMessage =
          error.response?.data?.detail || 'Đã xảy ra lỗi khi lưu mã giảm giá'
        ToastService.error(errorMessage)
      } finally {
        this.isSaving = false
      }
    },
    async toggleDiscountStatus (discount) {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const updatedStatus = !discount.is_active
        await axiosInstance.put(
          `${API_BASE_URL}/admin/discount-codes/${discount.discount_id}`,
          {
            ...discount,
            is_active: updatedStatus,
            // Convert dates to ISO format
            valid_from: discount.valid_from,
            valid_until: discount.valid_until
          },
          { headers }
        )

        // Update local state
        discount.is_active = updatedStatus

        ToastService.success(
          updatedStatus
            ? `Mã giảm giá ${discount.code} đã được kích hoạt`
            : `Mã giảm giá ${discount.code} đã bị vô hiệu hóa`
        )
      } catch (error) {
        console.error('Error toggling discount status:', error)
        ToastService.error('Không thể thay đổi trạng thái mã giảm giá')
      }
    },
    changePage(newPage) {
      this.currentPage = newPage
    }
  }
}
</script>

<style lang="scss" scoped>

@import '@/assets/styles/admin.scss';
@import '@/assets/styles/admin-table.scss';


.discount-codes-container {
  padding: 1.5rem;
  background-color: transparent;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h1 {
    font-size: 1.875rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
  }
}

.add-btn {
  background: linear-gradient(to right, #2563eb, #3b82f6);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.9375rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background: linear-gradient(to right, #1e40af, #2563eb);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
}

.filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.search-box {
  position: relative;
  width: 100%;
  max-width: 400px;

  input {
    width: 100%;
    padding: 0.75rem 0.75rem 0.75rem 2.25rem;
    border-radius: 0.5rem;
    border: 1px solid #e2e8f0;
    font-size: 0.9375rem;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
    }
  }

  i {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
  }
}

.filter-toggle {
  display: flex;
  gap: 0.5rem;

  .filter-btn {
    padding: 0.6rem 1rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    background-color: #f1f5f9;
    border: 1px solid #e2e8f0;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:hover:not(.active) {
      background-color: #e2e8f0;
    }

    &.active {
      background-color: #dbeafe;
      border-color: #93c5fd;
      color: #1d4ed8;
      font-weight: 500;
    }
  }
}

.loading,
.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #64748b;
  gap: 1rem;
  text-align: center;

  i {
    font-size: 2.5rem;
    opacity: 0.6;
  }

  p {
    font-size: 1.125rem;
    margin: 0.5rem 0 1rem 0;
  }
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
  margin-bottom: 1.5rem;
  -webkit-overflow-scrolling: touch; /* Cho phép cuộn mượt trên iOS */
}

.discount-codes-table table {
  min-width: 1000px; /* Đảm bảo bảng có chiều rộng tối thiểu để hiển thị đầy đủ */
}

.discount-codes-table {
  background-color: white;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
      padding: 1rem;
      text-align: left;
      border-bottom: 1px solid #f1f5f9;
      vertical-align: middle;
    }

    th {
      background-color: #f8fafc;
      font-weight: 600;
      color: #475569;
      font-size: 0.875rem;
      text-transform: uppercase;
      letter-spacing: 0.025em;
    }

    td {
      color: #334155;
      font-size: 0.9375rem;
    }

    tr:hover td {
      background-color: #f9fafb;
    }
  }
}

.code-cell {
  font-weight: 600;
  font-family: monospace;
  font-size: 1rem;
  color: #334155;
  padding: 0.25rem 0.5rem;
  background-color: #f1f5f9;
  border-radius: 0.25rem;
  letter-spacing: 0.05em;
  
  .internal-badge {
    display: inline-block;
    margin-left: 0.5rem;
    padding: 0.125rem 0.5rem;
    background: linear-gradient(135deg, #fed7aa 0%, #fbbf24 100%);
    color: #c2410c;
    font-size: 0.625rem;
    font-weight: 700;
    border-radius: 0.25rem;
    letter-spacing: 0.05em;
    vertical-align: middle;
    animation: pulse-glow 2s ease-in-out infinite;
  }
  
  .description-text {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 400;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 0 rgba(251, 191, 36, 0.4);
  }
  50% {
    box-shadow: 0 0 8px rgba(251, 191, 36, 0.8);
  }
}

.internal-row {
  background: linear-gradient(90deg, rgba(254, 243, 199, 0.3) 0%, transparent 100%);
  
  &:hover td {
    background: linear-gradient(90deg, rgba(254, 243, 199, 0.5) 0%, rgba(249, 250, 251, 1) 100%) !important;
  }
}

.max-discount {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.25rem;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;

  i {
    color: #94a3b8;
    font-size: 0.75rem;
  }
}

.expiry-status {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  display: inline-block;

  &.valid {
    background-color: #dcfce7;
    color: #15803d;
  }

  &.expiring-soon {
    background-color: #fef3c7;
    color: #b45309;
  }

  &.expired {
    background-color: #fee2e2;
    color: #b91c1c;
  }

  &.upcoming {
    background-color: #dbeafe;
    color: #1d4ed8;
  }

  &.unlimited {
    background-color: #f3e8ff;
    color: #7e22ce;
  }
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;

  &.active {
    background-color: #dcfce7;
    color: #15803d;
  }

  &.inactive {
    background-color: #f1f5f9;
    color: #64748b;
  }
}

.usage-warning {
  color: #b45309;
  font-weight: 600;
}

.action-cell {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.action-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &.edit-btn {
    background-color: #dbeafe;
    color: #1d4ed8;

    &:hover {
      background-color: #bfdbfe;
    }
  }

  &.deactivate-btn {
    background-color: #fee2e2;
    color: #b91c1c;

    &:hover {
      background-color: #fecaca;
    }
  }

  &.activate-btn {
    background-color: #dcfce7;
    color: #15803d;

    &:hover {
      background-color: #bbf7d0;
    }
  }
}

.add-btn-sm {
  padding: 0.625rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #2563eb;
  }
}

/* Modal Styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: block;
  align-items: flex-start; /* Thay đổi từ center thành flex-start */
  justify-content: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 20px 0
}

.modal-content {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 600px;
  margin: 0 auto 50px auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  position: relative;
  animation: modal-slide-in 0.3s ease-out forwards;
}

/* Đảm bảo modal luôn hiển thị đúng trên thiết bị di động */
@media (max-width: 640px) {
  .modal-content {
    width: 90%; /* Thu nhỏ kích thước trên màn hình nhỏ */
    margin: 1rem auto; /* Giảm margin trên màn hình nhỏ */
  }
}

@keyframes modal-slide-in {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(to right, #3b82f6, #60a5fa);
  color: white;
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;

  h2 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .close-btn {
    background: transparent;
    border: none;
    color: white;
    font-size: 1.25rem;
    cursor: pointer;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }
}

.modal-body {
  padding: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
  margin-bottom: 1.25rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.form-group {
  margin-bottom: 1.25rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #334155;
  }

  input,
  select {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    font-size: 0.9375rem;
    color: #334155;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
    }

    &:disabled {
      background-color: #f1f5f9;
      cursor: not-allowed;
    }
  }

  select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1rem center;
    background-size: 1rem;
    padding-right: 2.5rem;
  }

  .helper-text {
    display: block;
    margin-top: 0.375rem;
    font-size: 0.75rem;
    color: #64748b;
  }
}

.input-with-suffix {
  position: relative;

  input {
    padding-right: 3rem;
  }

  .suffix {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #64748b;
    font-size: 0.9375rem;
    pointer-events: none;
  }
}

.input-with-icon {
  position: relative;

  input,
  select {
    padding-left: 2.5rem;
  }

  .input-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #64748b;
    font-size: 1rem;
    pointer-events: none;
  }
}

.required {
  color: #e11d48;
  margin-left: 0.25rem;
}

.checkbox-control {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;

  input[type='checkbox'] {
    width: 1.25rem;
    height: 1.25rem;
    accent-color: #3b82f6;
  }

  label {
    margin: 0;
    cursor: pointer;
  }
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  
  &.internal-toggle {
    background: linear-gradient(135deg, #fff5f5 0%, #fff9e6 100%);
    border: 2px solid #fed7aa;
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1.5rem;
    
    .toggle-label span {
      color: #c2410c;
      font-weight: 600;
    }
    
    .internal-note {
      color: #ea580c;
      font-weight: 500;
      background: rgba(234, 88, 12, 0.1);
      padding: 0.5rem;
      border-radius: 0.25rem;
      margin-top: 0.5rem;
      display: block;
    }
  }
}

.toggle-switch {
  position: relative;
  width: 2.5rem;
  height: 1.25rem;
  background: #e2e8f0;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s;

  input[type='checkbox'] {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
  }

  label {
    position: absolute;
    top: 0;
    left: 0;
    width: 1.25rem;
    height: 1.25rem;
    background: white;
    border-radius: 999px;
    transition: all 0.2s;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  input[type='checkbox']:checked + label {
    transform: translateX(1.25rem);
    background: #3b82f6;
  }
  
  .internal-toggle & {
    input[type='checkbox']:checked + label {
      background: #ea580c;
    }
  }
}

.toggle-label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  span {
    font-weight: 500;
    color: #334155;
  }

  small {
    font-size: 0.75rem;
    color: #64748b;
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.cancel-btn,
.save-btn {
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: 500;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;

  &:hover {
    background: #e2e8f0;
  }
}

.save-btn {
  background: #3b82f6;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  i {
    font-size: 0.875rem;
  }
}

.uppercase {
  text-transform: uppercase;
}

/* Style cho phân trang */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  background-color: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  color: #4a5568;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: #f7fafc;
    border-color: #cbd5e0;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.page-info {
  font-weight: 500;
  color: #4a5568;
}

/* Media query cho màn hình nhỏ */
@media (max-width: 768px) {
  .filters {
    flex-direction: column;
    align-items: stretch;
    
    .search-box {
      max-width: none;
    }
  }
  
  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    
    .add-btn {
      width: 100%;
      justify-content: center;
    }
  }
  
  .pagination {
    padding: 0 1rem;
  }
}
</style>
